/**
 * Analytics Context
 *
 * Provides analytics functionality throughout the app.
 * Handles dataLayer initialization and user property synchronization.
 */

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAtomValue } from 'jotai';
import { useAuth } from './AuthContext';
import { subscriptionUsageAtom } from '../state/authAtoms';
import { featureFlags } from '../lib/featureFlags';
import { initializeDataLayer, setUserProperties, identifyUser, clearUserIdentity } from '../lib/analytics';

interface AnalyticsContextType {
  isEnabled: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType>({ isEnabled: false });

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const subscriptionUsage = useAtomValue(subscriptionUsageAtom);

  // Initialize dataLayer on mount
  useEffect(() => {
    initializeDataLayer();
  }, []);

  // Update user properties when auth state changes
  useEffect(() => {
    if (!featureFlags.analytics) return;

    if (user && profile) {
      // Identify the user
      identifyUser(user.id);

      // Determine subscription status
      let subscriptionStatus: 'active' | 'trial' | 'free' | 'cancelled' | 'expired' = 'free';
      let subscriptionPlan: 'free' | 'starter' | 'professional' | 'premium' = 'free';

      if (subscriptionUsage?.subscription) {
        const status = subscriptionUsage.subscription.status;
        if (status === 'active') subscriptionStatus = 'active';
        else if (status === 'trial') subscriptionStatus = 'trial';
        else if (status === 'cancelled') subscriptionStatus = 'cancelled';
        else if (status === 'expired') subscriptionStatus = 'expired';

        const planSlug = subscriptionUsage.plan?.slug;
        if (planSlug === 'starter') subscriptionPlan = 'starter';
        else if (planSlug === 'professional') subscriptionPlan = 'professional';
        else if (planSlug === 'premium') subscriptionPlan = 'premium';
      }

      // Set user properties for GA4
      setUserProperties({
        user_id: user.id,
        subscription_status: subscriptionStatus,
        subscription_plan: subscriptionPlan,
        // Note: lifetime_stagings would require a separate query to the database
        // For now, we track this per-session rather than lifetime
        lifetime_stagings: 0,
      });
    } else {
      // User logged out - clear identity
      clearUserIdentity();
    }
  }, [user, profile, subscriptionUsage]);

  return (
    <AnalyticsContext.Provider value={{ isEnabled: featureFlags.analytics }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalyticsContext() {
  return useContext(AnalyticsContext);
}
